<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900 cursor-pointer"
        :class="{ 'opacity-40 pointer-events-none': isBasic }"
        @click="handleClick"
      >
        Save
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    <div>Privacy controls</div>
    <Pill v-if="isBasic" class="ml-3" success>Pro only</Pill>
  </DialogHeader>
  <DialogContent>
    <UpgradeToProCallout v-if="isBasic" class="mb-5">
      Enable privacy/invite controls and other features
    </UpgradeToProCallout>

    <div :class="containerClasses">
      <div class="font-primary text-gray-800 mb-4">
        <div
          class="flex items-center font-semibold font-primary text-[14px] leading-[18px] mb-1"
        >
          <Icon :size="20" name="Lock" class="mr-2 stroke-gray-800" />
          <div>Control file visibility</div>
        </div>
        If private, only you and those you invite can see the selected file(s)
        and its description
      </div>

      <UiSelect
        v-model="visibility"
        label="Select file visibility"
        :options="options"
        :disabled="loading"
      />
    </div>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading || isBasic"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      :disabled="isBasic"
      :loading="loading"
      primary
      @click="handleClick"
    >
      Save
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { Icon, Pill, UiButton } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import UiSelect from '@/shared/components/UiSelect.vue'
import { computed, ref } from 'vue'
import {
  FilePrivacy,
  UserPermission,
  useUpdateFilePrivacyMutation,
} from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useUserStore } from '@/shared/stores/user'
import UpgradeToProCallout from '@/features/billing/components/UpgradeToProCallout.vue'

const options = [
  { label: 'Public', value: FilePrivacy.Public },
  { label: 'Private', value: FilePrivacy.Private },
]

const { close, open } = useDialog()

const props = defineProps<{
  ids: string[]
}>()

const { mutate: updateFilePrivacy, loading } = useUpdateFilePrivacyMutation()

const userStore = useUserStore()
const toastStore = useToastStore()

const isBasic = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Pro)
)

const containerClasses = computed(() => ({
  'pointer-events-none opacity-50': isBasic.value,
}))

const visibility = ref()

const handleClick = async () => {
  try {
    const response = await updateFilePrivacy({
      fileIds: props.ids,
      isPrivate: visibility.value === FilePrivacy.Private,
    })

    if (response?.data?.updateFilePrivacy.asyncProcess) {
      open(Dialogs.Success, {
        props: {
          title: 'Bulk change successful',
          html: 'Note: In some cases, it may take several minutes to process your request.',
        },
      })
    } else {
      close()

      toastStore.showToast({
        type: ToastType.Success,
        content: 'Successfully updated file privacy settings',
      })
    }
  } catch (error) {
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Could not update privacy settings',
    })
  }
}
</script>
